import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ThumbsUp,
  RefreshCw,
  Power,
  Save,
  Rocket,
  Trash2,
  Undo2,
  AlertCircle,
  CheckCircle2,
  Bot,
  Import,
  Lightbulb,
  BarChart3,
  Clock,
  ExternalLink,
  TrendingUp
} from "lucide-react";
import { Button } from "./ui/button";

// Pestaña «Radar Facebook» (solo admin). Habla solo con /api/admin/fb/*: nada de YouTube se toca desde aquí.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/fb`;

const authHeaders = () => {
  const token = localStorage.getItem("cortoviral_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const opts = () => ({ headers: authHeaders(), withCredentials: true });

const fmt = (n) => {
  if (n === null || n === undefined) return "—";
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace(".", ",")} M`;
  if (n >= 1e4) return `${Math.round(n / 1e3)} K`;
  return String(n);
};
const when = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : "nunca");

const STATUS_TABS = [
  { key: "ready", label: "Listos" },
  { key: "used", label: "Usados" },
  { key: "discarded", label: "Descartados" },
  { key: "failed", label: "Fallidos" }
];
const SOURCE = {
  radar_viral: { label: "Radar Viral", style: "text-red-300" },
  ideas: { label: "Idea de la IA", style: "text-amber-300" }
};

export const FacebookRadarView = ({ onGoToVideos }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("ready");
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async (statusFilter) => {
    try {
      const res = await axios.get(`${API}/radar`, { params: { status_filter: statusFilter }, ...opts() });
      setData(res.data);
      setForm((prev) => prev || res.data.settings);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "No se pudo cargar el Radar Facebook." });
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  const saveConfig = async (patch) => {
    setSaving(true);
    setFeedback(null);
    try {
      const body = patch || {
        niches: form.niches,
        import_from_radar: form.import_from_radar,
        ideas_enabled: form.ideas_enabled,
        ideas_per_run: Number(form.ideas_per_run),
        ideas_interval_hours: Number(form.ideas_interval_hours),
        reels_per_day: Number(form.reels_per_day),
        video_state: form.video_state,
        watermark: form.watermark
      };
      const res = await axios.post(`${API}/radar/config`, body, opts());
      setForm(res.data.settings);
      setData((d) => (d ? { ...d, ...res.data, topics: d.topics } : res.data));
      if (!patch) setFeedback({ type: "success", text: "Configuración guardada." });
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Error guardando la configuración." });
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (key, path, message) => {
    setAction(key);
    setFeedback(null);
    try {
      const res = await axios.post(`${API}/radar/${path}`, {}, opts());
      setFeedback({ type: res.data.errors?.length ? "error" : "success", text: message(res.data) });
      setTab("ready");
      load("ready");
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Ha habido un error." });
      load(tab);
    } finally {
      setAction(null);
    }
  };

  const createFromTopic = async (t) => {
    const where = data.connected ? `y se subirá a tu Página (${data.page_name})` : "(no se subirá: no hay ninguna Página conectada)";
    if (!window.confirm(`Se creará un Reel ORIGINAL sobre:\n\n"${t.topic_es}"\n\n${where}. ¿Continuar?`)) return;
    setBusyId(t.id);
    setFeedback(null);
    try {
      const res = await axios.post(`${API}/radar/topics/${t.id}/publish`, {}, opts());
      setFeedback({ type: "success", text: res.data.message || "Creando el Reel." });
      load(tab);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Error creando el Reel." });
    } finally {
      setBusyId(null);
    }
  };

  const decide = async (t, decision) => {
    setBusyId(t.id);
    try {
      await axios.post(`${API}/radar/topics/${t.id}/${decision}`, {}, opts());
      load(tab);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Error." });
    } finally {
      setBusyId(null);
    }
  };

  const toggleNiche = (key) => {
    setForm((f) => ({ ...f, niches: f.niches.includes(key) ? f.niches.filter((x) => x !== key) : [...f.niches, key] }));
  };

  if (!data || !form) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-sm gap-2">
        <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Cargando Radar Facebook...</span>
        {feedback && <span className="text-red-400 text-xs">{feedback.text}</span>}
      </div>
    );
  }

  const s = data.settings;
  const learn = data.learning;
  const cats = Object.entries(learn.categories || {}).sort((a, b) => b[1].mult - a[1].mult);
  const working = action !== null || data.working;

  return (
    <div data-testid="fb-radar-view" className="space-y-6">
      <div className="bg-gradient-to-br from-[#12141D] to-[#0F1A30] border border-blue-500/30 rounded-2xl p-6 shadow-2xl">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-500/15 border border-blue-500/40 text-blue-300 flex items-center justify-center shrink-0">
              <ThumbsUp className={`w-6 h-6 ${s.enabled ? "animate-pulse" : ""}`} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black text-white">Radar Facebook</h3>
                <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30">Solo admin</span>
                <span className={`text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full ${
                  s.enabled ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : "bg-slate-800 text-slate-400"
                }`}>
                  {s.enabled ? "Encendido · trabaja solo" : "Apagado"}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5 max-w-2xl">
                Facebook no deja ver qué es viral, así que los temas salen de tu Radar Viral de YouTube (solo se leen) y de ideas de la IA,
                y las estadísticas de tu Página deciden qué categorías van primero. No toca nada de YouTube.
              </p>
            </div>
          </div>
          <button
            data-testid="fb-radar-toggle-btn"
            role="switch"
            aria-checked={!!s.enabled}
            onClick={() => saveConfig({ enabled: !s.enabled })}
            disabled={saving}
            className={`h-9 pl-1 pr-3 rounded-full border flex items-center gap-2 text-xs font-black transition-colors disabled:opacity-60 self-start lg:self-auto ${
              s.enabled ? "bg-emerald-600/20 border-emerald-500/50 text-emerald-300" : "bg-slate-900 border-white/15 text-slate-400"
            }`}
          >
            <span className={`w-12 h-7 rounded-full relative transition-colors ${s.enabled ? "bg-emerald-500" : "bg-slate-700"}`}>
              <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow flex items-center justify-center transition-all ${s.enabled ? "left-6" : "left-1"}`}>
                <Power className={`w-3 h-3 ${s.enabled ? "text-emerald-600" : "text-slate-500"}`} />
              </span>
            </span>
            {s.enabled ? "ENCENDIDO" : "APAGADO"}
          </button>
        </div>

        {feedback && (
          <div className={`mt-4 p-3 rounded-xl text-xs flex items-start gap-2 break-words ${
            feedback.type === "success" ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" : "bg-red-500/10 border border-red-500/30 text-red-300"
          }`}>
            {feedback.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span className="min-w-0">{feedback.text}</span>
          </div>
        )}

        {!data.connected && (
          <div className="mt-4 p-3 rounded-xl text-xs flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-200">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              No hay ninguna Página conectada: sin ella no se suben Reels ni se leen estadísticas.{" "}
              {onGoToVideos && <button onClick={onGoToVideos} className="underline font-bold">Conectarla en la pestaña Facebook</button>}
            </span>
          </div>
        )}
        {s.import_from_radar && !data.main_radar_enabled && (
          <p className="mt-3 text-[11px] text-slate-400">
            Tu Radar Viral de YouTube está apagado: no habrá temas nuevos de ahí (los que ya encontró sí se importan). Las ideas de la IA siguen funcionando.
          </p>
        )}

        <div className="flex flex-wrap gap-2 mt-4">
          <Button onClick={() => doAction("sync", "sync", (r) => `Importados ${r.new} temas nuevos del Radar Viral (${r.seen} disponibles).`)}
            disabled={working} className="bg-blue-600 hover:bg-blue-500 text-white font-black text-xs flex items-center gap-1.5">
            {action === "sync" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Import className="w-3.5 h-3.5" />} Importar del Radar Viral
          </Button>
          <Button onClick={() => doAction("ideas", "ideas", (r) => `La IA (${r.engine}) ha propuesto ${r.added} temas nuevos.`)}
            disabled={working} className="bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center gap-1.5 border border-white/10">
            {action === "ideas" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Lightbulb className="w-3.5 h-3.5" />} Pedir ideas a la IA
          </Button>
          <Button onClick={() => doAction("insights", "insights", (r) => `Estadísticas de ${r.updated} Reels actualizadas.` + (r.errors?.length ? ` Avisos: ${r.errors.join("; ")}` : ""))}
            disabled={working || !data.connected} className="bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center gap-1.5 border border-white/10">
            {action === "insights" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <BarChart3 className="w-3.5 h-3.5" />} Actualizar estadísticas
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
          <div className="p-3.5 rounded-xl bg-black/40 border border-white/5">
            <span className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-blue-300" /> Temas</span>
            <span className="text-xs text-white block mt-1.5">
              <strong className="text-emerald-400">{data.counts.ready}</strong> listos · {data.counts.used} usados · {data.counts.discarded} descartados
            </span>
            <span className="text-[10px] text-slate-500 block mt-0.5">Importado: {when(s.last_sync)} · Ideas: {when(s.last_ideas)}</span>
            {s.last_error && <span className="text-[10px] text-red-400 block mt-0.5 break-words">{s.last_error}</span>}
          </div>
          <div className="p-3.5 rounded-xl bg-black/40 border border-white/5">
            <span className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5 text-cyan-400" /> Aprendizaje</span>
            <span className="text-xs text-white block mt-1.5">{learn.measured} de {learn.published} Reels medidos</span>
            <span className="text-[10px] text-slate-500 block mt-0.5">
              Estadísticas: {when(s.last_insights)}. Cuentan los Reels con más de 24 h.
            </span>
          </div>
          <div className="p-3.5 rounded-xl bg-black/40 border border-white/5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1.5"><Bot className="w-3.5 h-3.5 text-blue-300" /> Piloto Facebook</span>
              <button
                data-testid="fb-autopilot-toggle"
                role="switch"
                aria-checked={!!s.autopilot_enabled}
                onClick={() => saveConfig({ autopilot_enabled: !s.autopilot_enabled })}
                disabled={saving}
                className={`w-10 h-6 rounded-full relative transition-colors shrink-0 ${s.autopilot_enabled ? "bg-emerald-500" : "bg-slate-700"}`}
                title={s.autopilot_enabled ? "Apagar el Piloto Facebook" : "Encender el Piloto Facebook"}
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${s.autopilot_enabled ? "left-5" : "left-1"}`} />
              </button>
            </div>
            <span className="text-xs text-white block mt-1.5">
              {s.autopilot_enabled ? `${s.reels_per_day} Reels al día con los temas de más prioridad` : "Apagado"}
            </span>
            <span className="text-[10px] text-slate-500 block mt-0.5">
              {s.autopilot_enabled && data.next_autopilot ? `Próximo: ${when(data.next_autopilot)}` : "Publica solo en Facebook. Los Pilotos de YouTube siguen igual."}
            </span>
            {data.busy && <span className="text-[10px] text-blue-300 block mt-0.5">Ahora mismo se está creando un Reel…</span>}
          </div>
        </div>
      </div>

      <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-4">
        <h4 className="text-sm font-black text-white flex items-center gap-1.5"><BarChart3 className="w-4 h-4 text-blue-300" /> Qué funciona en tu Página</h4>
        {learn.measured === 0 ? (
          <p className="text-xs text-slate-500">
            Todavía no hay datos. Cuando tus Reels lleven más de 24 h publicados, aquí verás qué categorías tienen más reproducciones,
            a qué horas y cuáles son tus mejores Reels. Mientras tanto, todas las categorías tienen la misma prioridad.
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-1.5">
              <span className="text-[11px] uppercase font-bold text-slate-400 block">Categorías (multiplicador de prioridad)</span>
              {cats.map(([key, c]) => (
                <div key={key} className="flex items-center gap-2 text-xs">
                  <span className="w-32 shrink-0 truncate text-slate-300">{c.label}</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
                    <div className={`h-full ${c.mult >= 1 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.round(((c.mult - 0.6) / 1.0) * 100)}%` }} />
                  </div>
                  <span className={`w-12 text-right font-mono ${c.mult >= 1 ? "text-emerald-300" : "text-amber-300"}`}>×{String(c.mult).replace(".", ",")}</span>
                  <span className="w-28 text-right text-[10px] text-slate-500 font-mono">{c.n} {c.n === 1 ? "Reel" : "Reels"} · {fmt(c.avg_plays)}</span>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-[11px] uppercase font-bold text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Mejores horas (España)</span>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {learn.hours.map((h) => (
                    <span key={h.hour} className="text-[11px] font-mono px-2 py-0.5 rounded bg-blue-500/15 text-blue-200 border border-blue-500/30">
                      {String(h.hour).padStart(2, "0")}:00 · {fmt(h.avg_plays)}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-[11px] uppercase font-bold text-slate-400 block">Mejores Reels</span>
                <ul className="mt-1 space-y-1">
                  {learn.best.map((b) => (
                    <li key={b.id} className="text-[11px] text-slate-300 flex items-center gap-1.5 min-w-0">
                      <span className="font-mono text-emerald-300 shrink-0">{fmt(b.plays)}</span>
                      {b.fb_url ? (
                        <a href={b.fb_url} target="_blank" rel="noopener noreferrer" className="truncate hover:text-white flex items-center gap-1">
                          {b.title} <ExternalLink className="w-3 h-3 shrink-0" />
                        </a>
                      ) : <span className="truncate">{b.title}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-5">
        <h4 className="text-sm font-black text-white">Configuración</h4>
        <div>
          <span className="text-[11px] uppercase font-bold text-slate-400 block mb-2">
            Categorías que usa {form.niches.length === 0 && <span className="text-blue-300 normal-case">(todas)</span>}
          </span>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setForm((f) => ({ ...f, niches: [] }))}
              className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${
                form.niches.length === 0 ? "bg-blue-600 text-white border-blue-500" : "bg-slate-900 text-slate-400 border-white/10 hover:text-white"
              }`}
            >
              Todas
            </button>
            {Object.entries(data.niches).map(([key, label]) => (
              <button
                key={key}
                onClick={() => toggleNiche(key)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${
                  form.niches.includes(key) ? "bg-blue-500/20 text-blue-300 border-blue-500/50" : "bg-slate-900 text-slate-400 border-white/10 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 text-xs text-slate-300">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!form.import_from_radar} onChange={(e) => setForm((f) => ({ ...f, import_from_radar: e.target.checked }))} className="accent-blue-500 w-4 h-4" />
            Importar temas del Radar Viral (cada hora)
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!form.ideas_enabled} onChange={(e) => setForm((f) => ({ ...f, ideas_enabled: e.target.checked }))} className="accent-blue-500 w-4 h-4" />
            Pedir ideas a la IA cuando queden menos de 10 temas
          </label>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { field: "ideas_per_run", label: "Ideas por petición", min: 1, max: 10 },
            { field: "ideas_interval_hours", label: "Ideas cada (h)", min: 6, max: 168 },
            { field: "reels_per_day", label: "Reels al día (Piloto)", min: 1, max: 10 }
          ].map(({ field, label, min, max }) => (
            <label key={field} className="block">
              <span className="text-[11px] uppercase font-bold text-slate-400 block mb-1">{label}</span>
              <input
                type="number"
                min={min}
                max={max}
                value={form[field]}
                onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500/60"
              />
            </label>
          ))}
          <label className="block">
            <span className="text-[11px] uppercase font-bold text-slate-400 block mb-1">Marca de agua</span>
            <input
              type="text"
              maxLength={40}
              placeholder="fb.com/TuPagina"
              value={form.watermark}
              onChange={(e) => setForm((f) => ({ ...f, watermark: e.target.value }))}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500/60"
            />
          </label>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-white/5">
          <label className="flex flex-wrap items-center gap-2 text-xs text-slate-300 min-w-0">
            Al subir:
            <select
              value={form.video_state}
              onChange={(e) => setForm((f) => ({ ...f, video_state: e.target.value }))}
              title="Los borradores se revisan y publican desde Meta Business Suite"
              className="max-w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
            >
              <option value="PUBLISHED">Publicar directamente</option>
              <option value="DRAFT">Borrador (revisar antes)</option>
            </select>
          </label>
          <Button
            data-testid="fb-radar-save-btn"
            onClick={() => saveConfig()}
            disabled={saving}
            className="bg-slate-100 hover:bg-white text-slate-950 font-bold text-xs flex items-center gap-1.5"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Guardando..." : "Guardar configuración"}
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-lg font-semibold whitespace-nowrap ${
                tab === t.key ? "bg-blue-600 text-white" : "bg-slate-900/60 text-slate-400 hover:text-white"
              }`}
            >
              {t.label} ({data.counts[t.key] ?? 0})
            </button>
          ))}
        </div>

        {data.topics.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-500 bg-[#12141D] border border-white/5 rounded-2xl">
            No hay temas en esta lista. Pulsa «Importar del Radar Viral» o «Pedir ideas a la IA».
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {data.topics.map((t) => {
              const src = SOURCE[t.source] || SOURCE.ideas;
              return (
                <div key={t.id} className="bg-[#12141D] border border-white/10 rounded-xl p-3 space-y-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-200 border border-blue-500/30" title="Prioridad (puntuación × multiplicador de la categoría)">
                      {Math.round(t.priority)}
                    </span>
                    <span className="text-[10px] uppercase font-bold text-blue-300">{data.niches[t.category] || t.category}</span>
                    <span className={`text-[10px] font-bold ${src.style}`}>{src.label}</span>
                    <span className="text-[10px] text-slate-500">{when(t.found_at)}</span>
                    {t.status === "ready" && !t.fresh && <span className="text-[10px] text-amber-400">caducado: el Piloto ya no lo usa</span>}
                  </div>
                  <p className="text-sm font-bold text-white leading-snug break-words">{t.topic_es}</p>
                  {t.ref_title && <p className="text-[11px] text-slate-500 truncate">Tendencia de origen: {t.ref_title}{t.ref_views ? ` · ${fmt(t.ref_views)} visitas` : ""}</p>}
                  {t.reason && <p className="text-[11px] text-slate-500 break-words">{t.reason}</p>}
                  {t.last_error && t.status !== "used" && <p className="text-[11px] text-red-400 break-words">{t.last_error}</p>}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {(t.status === "ready" || t.status === "failed") && (
                      <Button
                        size="sm"
                        onClick={() => createFromTopic(t)}
                        disabled={busyId !== null || data.busy}
                        title={data.busy ? "Ya se está creando un Reel; espera a que termine" : ""}
                        className="h-7 px-2.5 text-[11px] font-bold bg-blue-600 hover:bg-blue-500 text-white flex items-center gap-1"
                      >
                        {busyId === t.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Rocket className="w-3 h-3" />}
                        Crear y subir a Facebook
                      </Button>
                    )}
                    {t.status !== "discarded" && t.status !== "used" && (
                      <Button size="sm" variant="ghost" onClick={() => decide(t, "discard")} disabled={busyId !== null}
                        className="h-7 px-2 text-[11px] text-slate-400 hover:text-white flex items-center gap-1">
                        <Trash2 className="w-3 h-3" /> Descartar
                      </Button>
                    )}
                    {t.status === "discarded" && (
                      <Button size="sm" variant="ghost" onClick={() => decide(t, "restore")} disabled={busyId !== null}
                        className="h-7 px-2 text-[11px] text-slate-400 hover:text-white flex items-center gap-1">
                        <Undo2 className="w-3 h-3" /> Recuperar
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
